import type { ReactNode } from "react";
import type {
	EntryShared,
	OptionalSubmodule,
	StartWithWithoutTierTwo,
	T2Schema,
	TranslationKey,
} from "@typedefs/modulesInterfaces";
import { Form, useFormikContext, getIn, FieldArray } from "formik";
import { createContext, useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useContextAwareGetSubmoduleDefaults } from "@hooks/index";
import { useCustomTranslation, usePermissions, useWarningMessageT2 } from "@app/hooks";
import { handleDisableSww, handleElectricityDisable } from "./moduleUtils";
import { preventFormSubmit } from "./inputs/utils";
import { TranslatedFormSubmodule } from "@components/formSubmodule/TranslatedFormSubmodule";
import { useMissingEmissionFactorForCountry } from "@utils/useMissingEmissionFactorForCountry";
import { isEqual } from "lodash";
import FormSubmodule from "@components/formSubmodule/FormSubmodule";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import DefaultsErrorNote from "@components/messageNote/DefaultsErrorNote";
import LoadDefaultsButton from "./LoadDefaultsButton";
import useErrorMessage from "@utils/useErrorMessage";
import FadeAnimation from "@components/animations/FadeAnimation";

const variants = {
	open: {
		width: "100%",
		transition: {
			type: "spring",
			stiffness: 20,
			restDelta: 2,
		},
		closed: {
			width: 0,
			transition: {
				delay: 0.5,
				type: "spring",
				stiffness: 400,
				damping: 40,
			},
		},
	},
};
type GenericT2Context = {
	isReadOnly: boolean;
}
const GenericTwoContext = createContext<GenericT2Context>({
	isReadOnly: false
});

const GenericTierTwo = ({
	title,
	t2Schema,
	tabsOpen,
	warningMessage,
}: {
	title: TranslationKey;
	t2Schema: T2Schema;
	tabsOpen: boolean;
	warningMessage?: string | null
}) => {
	const { values } = useFormikContext();

	const { isReadOnly } = usePermissions();
	const { warningMessages, handleDefaultsError } = useWarningMessageT2();
	const translatedTitle = useCustomTranslation(title);
	if (tabsOpen) return null;
	return (
		<GenericTwoContext.Provider value={{ isReadOnly }}>
			<motion.div initial={false} variants={variants} className="w-100 d-flex justify-content-end">
				<Form onKeyDown={preventFormSubmit} className="py-2 w-100">
					<div className="pos-relative mb-2 pb-1">
						<h2 className="fs-13 ff-normal-ext module-header w-fit">
							{translatedTitle} - Tier 2
						</h2>
						{warningMessage && <DefaultsErrorNote noteMessage={warningMessage} />}
						{warningMessages.map((warning, index) => (
							<FadeAnimation key={warning.id} delay={index * 0.1} isShown>
								<DefaultsErrorNote key={warning.id} noteMessage={warning.message} />
							</FadeAnimation>
						))}
					</div>

					<LoadDefaultsButton />

					{t2Schema.submodules.map((submodule) => {
						return (
							<FormSubmodule submoduleName={submodule.name} key={submodule.name}>
								<TierTwoBlock>
									{submodule.inputGroups.map((input, index) => {
										const curatedInput = handleDisableSww({ input, isReadOnly, index });
										return (
											<GenericInputGroup
												key={`submodule-${index}-${submodule.name}`}
												inputGroup={{ ...curatedInput, disabled: isReadOnly }} //, autosubmit: true
											/>
										)
									})}

									{submodule.conditionalSections?.map((section) => (
										<FadeAnimation isShown={getIn(values, section.conditionName)}>
											<>
												{section.inputGroups.map((input, i) => (
													<div key={`${input.label}-${i}`} className="px-2">
														<GenericInputGroup inputGroup={{ ...input, disabled: isReadOnly }} />
													</div>
												))}
											</>
										</FadeAnimation>
									))}
								</TierTwoBlock>
							</FormSubmodule>
						)
					})}
					{t2Schema.optionalSubmodules?.map((submodule, index) => (
						<FormOptionalSubmodules
							submodule={submodule}
							key={`optsubmodule-${index}-${submodule.name}`}
							onDefaultsError={handleDefaultsError}
						/>
					))}
				</Form>
			</motion.div>
		</GenericTwoContext.Provider>
	);
};

const FormOptionalSubmodules = <T,>({ submodule, onDefaultsError }: { submodule: OptionalSubmodule<T>, onDefaultsError: (message: string) => void }) => {
	const { values } = useFormikContext();
	const { isReadOnly } = useContext(GenericTwoContext);
	const submoduleValues = getIn(values, submodule.inputName);

	const [list, setList] = useState<any[]>([]);
	useMissingEmissionFactorForCountry({ handleDefaultsError: onDefaultsError, listInputName: submodule.inputName })

	useEffect(() => {
		const newList = getIn(values, submodule.inputName)
		const isListUpdated = !isEqual(list, newList)
		if (isListUpdated) {
			setList(newList)
		}
	}, [values, submodule.inputName]);

	return Array.isArray(submoduleValues) ? (
		<>
			<FieldArray name={submodule.inputName}>
				{() => (
					<>
						{list.map((el, index) => {
							return el.id ? (
								<FormOptionalSubmoduleELement
									entry={el}
									submodule={submodule}
									index={index}
									submoduleName={submodule.name}
									key={`${index + 1}. ${submodule.name}`}
									id={el.id}
									onDefaultsError={onDefaultsError}
								/>
							) : null;
						})}
					</>
				)}
			</FieldArray>
		</>
	) : (
		<FormSubmodule submoduleName={submodule.name}>
			<TierTwoBlock>
				{submodule.inputGroups.map((input, i) => {
					const curatedInput = handleDisableSww({ input, isReadOnly, index: i });
					return (
						<GenericInputGroup
							key={`${submodule.name}-${i}`}
							inputGroup={{ ...curatedInput, disabled: isReadOnly }} //, autosubmit: true
						/>
					)
				})}
			</TierTwoBlock>
		</FormSubmodule>
	);
};

const FormOptionalSubmoduleELement = <T,>({
	submodule,
	entry,
	index,
	submoduleName,
	id,
	onDefaultsError
}: {
	submodule: OptionalSubmodule<T>;
	entry: EntryShared;
	index: number;
	submoduleName: TranslationKey;
	id: number;
	onDefaultsError?: (message: string) => void
}) => {
	const { isReadOnly } = useContext(GenericTwoContext);
	const { setFieldValue, values } = useFormikContext<any>();
	const currentEntry = getIn(values, `${submodule.inputName}.${index}`) ?? entry;
	const conditionValues = (submodule.conditionalSections ?? []).map((section) =>
		Boolean(getIn(values, `${submodule.inputName}.${index}.${section.conditionName}`)),
	);
	const electricityFlags = {
		is_start: Boolean(getIn(values, `${submodule.inputName}.${index}.is_electricity_start`)),
		is_w: Boolean(getIn(values, `${submodule.inputName}.${index}.is_electricity_w`)),
		is_wo: Boolean(getIn(values, `${submodule.inputName}.${index}.is_electricity_wo`)),
	};
	const { data: defaults, isError, error } = useContextAwareGetSubmoduleDefaults({
		uniqueId: id,
		route: submodule.route ?? "",
		cacheTag: submodule.cacheTag ?? "FakeTag",
	},
		{ skip: !id || !submodule.route || !submodule.cacheTag },
	);

	//conditional section
	useEffect(() => {
		for (const section of submodule.conditionalSections ?? []) {
			const sectionVisible = Boolean(getIn(values, `${submodule.inputName}.${index}.${section.conditionName}`));
			if (section.initialValues && !sectionVisible) {
				const nextEntry = { ...currentEntry, ...section.initialValues };
				if (!isEqual(currentEntry, nextEntry)) {
					setFieldValue(`${submodule.inputName}.${index}`, nextEntry)
				}
			}
		}
	}, [submodule.conditionalSections, conditionValues.join("|"), setFieldValue, values, submodule.inputName, index])

	//defaults handling
	useEffect(() => {
		if (!defaults || !setFieldValue || !submodule.route) return;
		const entryWithDefaults = defaults ? { ...currentEntry, ...defaults } : null
		if (entryWithDefaults && index !== undefined && !isEqual(currentEntry, entryWithDefaults))
			setFieldValue(`${submodule.inputName}.${index}`, entryWithDefaults);
	}, [defaults, setFieldValue, index, submodule.route, submodule.inputName]);

	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError, error });
	useEffect(() => {
		if (defaultsErrorMsg && onDefaultsError) onDefaultsError(defaultsErrorMsg);
	}, [defaultsErrorMsg, onDefaultsError])


	return (
		<TranslatedFormSubmodule
			submoduleName={submoduleName}
			itemId={id}
			itemIndex={index + 1}
			className="pt-2"
		>
			<TierTwoBlock>
				{submodule.inputGroups.map((input, i) => {
					const curatedInput = handleDisableSww({ input, isReadOnly, index });
					const electricityHandledInput = handleElectricityDisable({
						input: curatedInput as StartWithWithoutTierTwo,
						electricityFlags,
						isReadOnly,
					});
					return (
						<GenericInputGroup
							key={`${submodule.name}-${i}`}
							inputGroup={{ ...electricityHandledInput, index, disabled: isReadOnly }} //, autosubmit: true
						/>
					)
				})}

				{submodule.conditionalSections?.map((section) => (
					<FadeAnimation
						isShown={Boolean(getIn(values, `${submodule.inputName}.${index}.${section.conditionName}`))}
						key={`${section.conditionName}-${index}`}
					>
						<>
							{section.inputGroups.map((input) => {
								const curatedInput = handleDisableSww({ input, isReadOnly, index });
								const electricityHandledInput = handleElectricityDisable({
									input: curatedInput as StartWithWithoutTierTwo,
									electricityFlags,
									isReadOnly,
								});
								return (
									<GenericInputGroup
										key={`${input.label}-${entry.id}`}
										inputGroup={{ ...electricityHandledInput, disabled: isReadOnly, index }}
									/>
								);
							})}
						</>
					</FadeAnimation>
				))}
			</TierTwoBlock>
		</TranslatedFormSubmodule>
	);
};

export const TierTwoBlock = ({ children }: { children: ReactNode }) => {
	return <div className="tiertwo-block">{children}</div>;
};

export default GenericTierTwo;
